import {
  type CaptionItem,
  type ClipItem,
  type Edl,
  type GifItem,
  type Item,
  type SfxItem,
  type TitleOverlay,
  type Track,
  type TransitionItem,
  DEFAULT_CAPTION_STYLE,
  DEFAULT_SFX_FRAMES,
  DEFAULT_TITLE_STYLE,
  DEFAULT_TRACK_IDS,
  TRACK_ITEM_TYPES,
  findTrack,
  itemEnd,
  locateItem,
  framesToSec,
  secToFrames,
} from './edl';
import { type EditCommand, editCommandSchema } from './commands';
import { EditError } from './errors';
import { nextId } from './ids';
import { SFX_LIBRARY } from './sfx';

// ── Invariant helpers ────────────────────────────────────────

/** Sort items by `from` asc, ties broken by `id` — deterministic ordering. */
function sortItems(items: Item[]): Item[] {
  return [...items].sort((a, b) => a.from - b.from || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** Whether two timeline intervals [from, end) overlap. */
function overlaps(aFrom: number, aEnd: number, bFrom: number, bEnd: number): boolean {
  return aFrom < bEnd && bFrom < aEnd;
}

/**
 * Reject if a candidate clip would overlap another clip on a video track.
 * Only clips collide with clips; transitions intentionally straddle the cut.
 */
function assertNoClipOverlap(
  track: Track,
  candidateFrom: number,
  candidateDur: number,
  ignoreId: string,
  op: string
): void {
  if (track.kind !== 'video' && track.kind !== 'broll') return;
  const cEnd = candidateFrom + candidateDur;
  for (const other of track.items) {
    if (other.type !== 'clip' || other.id === ignoreId) continue;
    if (overlaps(candidateFrom, cEnd, other.from, itemEnd(other))) {
      throw new EditError(
        'OVERLAP',
        `clip would overlap "${other.id}" on track "${track.id}"`,
        op,
        'at'
      );
    }
  }
}

function recomputeDuration(tracks: Track[]): number {
  let max = 0;
  for (const track of tracks) {
    for (const item of track.items) {
      const end = itemEnd(item);
      if (end > max) max = end;
    }
  }
  return max;
}

/** Return a new EDL with replaced tracks (normalized) + recomputed duration. */
function commit(edl: Edl, tracks: Track[], seq: number): Edl {
  const normalized = tracks.map((t) => ({ ...t, items: sortItems(t.items) }));
  return { ...edl, tracks: normalized, seq, durationInFrames: recomputeDuration(normalized) };
}

function requireTrack(edl: Edl, trackId: string, op: string): Track {
  const track = edl.tracks.find((t) => t.id === trackId);
  if (!track) {
    throw new EditError('TRACK_NOT_FOUND', `no track "${trackId}"`, op, 'trackId');
  }
  return track;
}

function replaceItems(tracks: Track[], trackId: string, items: Item[]): Track[] {
  return tracks.map((t) => (t.id === trackId ? { ...t, items } : t));
}

// ── Reducer ──────────────────────────────────────────────────

/**
 * The ONLY way to mutate an EDL. Pure: never touches `edl`, always returns a
 * fresh object. Validates `cmd` against the Zod schema first, then applies it.
 * Throws {@link EditError} on any rejection without mutating anything.
 */
export function apply(edl: Edl, cmd: EditCommand): Edl {
  const parsed = editCommandSchema.safeParse(cmd);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const field = issue?.path.join('.') || undefined;
    const opLabel = (cmd as { op?: string })?.op ?? 'unknown';
    throw new EditError(
      'INVALID_COMMAND',
      issue ? `${field ? `${field}: ` : ''}${issue.message}` : 'invalid command',
      opLabel,
      field
    );
  }
  const c = parsed.data;

  switch (c.op) {
    case 'addClip': {
      const track = requireTrack(edl, c.trackId, c.op);
      if (!TRACK_ITEM_TYPES[track.kind].includes('clip')) {
        throw new EditError(
          'TRACK_KIND_MISMATCH',
          `track "${track.id}" cannot hold clips`,
          c.op,
          'trackId'
        );
      }
      const dur = secToFrames(c.source.sourceOutSec - c.source.sourceInSec);
      if (dur <= 0) {
        throw new EditError(
          'EMPTY_RESULT',
          'clip source range rounds to zero frames',
          c.op,
          'source'
        );
      }
      assertNoClipOverlap(track, c.at, dur, '', c.op);
      const { id, seq } = nextId(edl, 'clip');
      const clip: ClipItem = {
        id,
        type: 'clip',
        from: c.at,
        durationInFrames: dur,
        source: { ...c.source },
        ...(c.crop ? { crop: { ...c.crop } } : {}),
        ...(c.fit ? { fit: c.fit } : {}),
        ...(c.volume !== undefined ? { volume: c.volume } : {}),
        ...(c.rect ? { rect: { ...c.rect } } : {}),
      };
      return commit(edl, replaceItems(edl.tracks, track.id, [...track.items, clip]), seq);
    }

    case 'split': {
      const loc = locateItem(edl, c.itemId);
      if (!loc) throw new EditError('ITEM_NOT_FOUND', `no item "${c.itemId}"`, c.op, 'itemId');
      const { item, track } = loc;
      if (item.type !== 'clip' && item.type !== 'caption') {
        throw new EditError('TRACK_KIND_MISMATCH', `cannot split a ${item.type}`, c.op, 'itemId');
      }
      if (c.atFrame <= item.from || c.atFrame >= itemEnd(item)) {
        throw new EditError(
          'OUT_OF_BOUNDS',
          `split point ${c.atFrame} must be strictly inside [${item.from}, ${itemEnd(item)})`,
          c.op,
          'atFrame'
        );
      }
      const leftDur = c.atFrame - item.from;
      const rightDur = itemEnd(item) - c.atFrame;
      const { id: rightId, seq } = nextId(edl, item.type);

      let left: Item;
      let right: Item;
      if (item.type === 'clip') {
        const span = item.source.sourceOutSec - item.source.sourceInSec;
        const midSec = item.source.sourceInSec + (leftDur / item.durationInFrames) * span;
        left = {
          ...item,
          durationInFrames: leftDur,
          source: { ...item.source, sourceOutSec: midSec },
        };
        right = {
          ...item,
          id: rightId,
          from: c.atFrame,
          durationInFrames: rightDur,
          source: { ...item.source, sourceInSec: midSec },
        };
      } else {
        left = { ...item, durationInFrames: leftDur };
        right = { ...item, id: rightId, from: c.atFrame, durationInFrames: rightDur };
      }
      const items = track.items.flatMap((it) => (it.id === item.id ? [left, right] : [it]));
      return commit(edl, replaceItems(edl.tracks, track.id, items), seq);
    }

    case 'trim': {
      const loc = locateItem(edl, c.itemId);
      if (!loc) throw new EditError('ITEM_NOT_FOUND', `no item "${c.itemId}"`, c.op, 'itemId');
      const { item, track } = loc;

      if ((c.sourceInSec !== undefined || c.sourceOutSec !== undefined) && item.type !== 'clip') {
        throw new EditError(
          'TRACK_KIND_MISMATCH',
          `cannot trim source of a ${item.type}`,
          c.op,
          'itemId'
        );
      }

      let next: Item = { ...item };
      if (item.type === 'clip' && (c.sourceInSec !== undefined || c.sourceOutSec !== undefined)) {
        const sourceInSec = c.sourceInSec ?? item.source.sourceInSec;
        const sourceOutSec = c.sourceOutSec ?? item.source.sourceOutSec;
        if (sourceOutSec <= sourceInSec) {
          throw new EditError(
            'INVALID_SOURCE_RANGE',
            'sourceOutSec must exceed sourceInSec',
            c.op,
            'sourceOutSec'
          );
        }
        next = { ...(next as ClipItem), source: { ...item.source, sourceInSec, sourceOutSec } };
        if (c.durationInFrames === undefined) {
          next.durationInFrames = secToFrames(sourceOutSec - sourceInSec);
        }
      }
      if (c.durationInFrames !== undefined) {
        next.durationInFrames = c.durationInFrames;
        // 클립은 타임라인 길이와 소스 구간이 **항상 일치**해야 한다: 렌더러는 세그먼트를
        // 소스 길이(sourceOut-sourceIn)로 자르는 반면, 오디오·자막은 타임라인 길이를
        // 기준으로 놓인다. 어긋나면 그림과 소리의 시계가 갈라지고, 프리뷰에서 짧게 보이던
        // 클립이 익스포트에서 길게 나온다. 길이를 줄이면 소스 뒷부분을 그만큼 잘라낸다.
        if (next.type === 'clip') {
          const inSec = next.source.sourceInSec;
          next = {
            ...next,
            source: { ...next.source, sourceOutSec: inSec + framesToSec(c.durationInFrames) },
          };
        }
      }
      if (next.durationInFrames <= 0) {
        throw new EditError(
          'EMPTY_RESULT',
          'trim would produce a zero-length item',
          c.op,
          'durationInFrames'
        );
      }
      if (next.type === 'clip' && next.source.sourceOutSec <= next.source.sourceInSec) {
        throw new EditError(
          'INVALID_SOURCE_RANGE',
          'trim would produce an empty source range',
          c.op,
          'durationInFrames'
        );
      }
      if (next.type === 'clip' && next.source.sourceInSec < 0) {
        throw new EditError(
          'INVALID_SOURCE_RANGE',
          'sourceInSec cannot be negative — the source has nothing before 0',
          c.op,
          'sourceInSec'
        );
      }
      if (item.type === 'clip') {
        assertNoClipOverlap(track, next.from, next.durationInFrames, item.id, c.op);
      }
      const items = track.items.map((it) => (it.id === item.id ? next : it));
      return commit(edl, replaceItems(edl.tracks, track.id, items), edl.seq);
    }

    case 'move': {
      const loc = locateItem(edl, c.itemId);
      if (!loc) throw new EditError('ITEM_NOT_FOUND', `no item "${c.itemId}"`, c.op, 'itemId');
      const target = requireTrack(edl, c.toTrackId, c.op);
      if (!TRACK_ITEM_TYPES[target.kind].includes(loc.item.type)) {
        throw new EditError(
          'TRACK_KIND_MISMATCH',
          `track "${target.id}" (${target.kind}) cannot hold a ${loc.item.type}`,
          c.op,
          'toTrackId'
        );
      }
      const moved: Item = { ...loc.item, from: c.at };
      if (moved.type === 'clip') {
        assertNoClipOverlap(target, moved.from, moved.durationInFrames, moved.id, c.op);
      }
      // Remove from source track, then add to target (handles same-track reposition).
      let tracks = replaceItems(
        edl.tracks,
        loc.track.id,
        loc.track.items.filter((it) => it.id !== loc.item.id)
      );
      const targetAfterRemoval = tracks.find((t) => t.id === target.id);
      tracks = replaceItems(tracks, target.id, [...(targetAfterRemoval?.items ?? []), moved]);
      return commit(edl, tracks, edl.seq);
    }

    case 'remove': {
      const loc = locateItem(edl, c.itemId);
      if (!loc) throw new EditError('ITEM_NOT_FOUND', `no item "${c.itemId}"`, c.op, 'itemId');
      // Cascade: drop transitions that reference the removed item.
      const tracks = edl.tracks.map((t) => ({
        ...t,
        items: t.items.filter((it) => {
          if (it.id === c.itemId) return false;
          if (
            it.type === 'transition' &&
            (it.afterItemId === c.itemId || it.beforeItemId === c.itemId)
          ) {
            return false;
          }
          return true;
        }),
      }));
      return commit(edl, tracks, edl.seq);
    }

    case 'addTransition': {
      const after = locateItem(edl, c.afterItemId);
      const before = locateItem(edl, c.beforeItemId);
      if (!after)
        throw new EditError('ITEM_NOT_FOUND', `no item "${c.afterItemId}"`, c.op, 'afterItemId');
      if (!before)
        throw new EditError('ITEM_NOT_FOUND', `no item "${c.beforeItemId}"`, c.op, 'beforeItemId');
      if (
        after.track.id !== before.track.id ||
        after.item.type !== 'clip' ||
        before.item.type !== 'clip'
      ) {
        throw new EditError(
          'NOT_ADJACENT',
          'transition requires two clips on the same track',
          c.op,
          'beforeItemId'
        );
      }
      const ordered = sortItems(after.track.items.filter((it) => it.type === 'clip'));
      const afterIdx = ordered.findIndex((it) => it.id === after.item.id);
      if (afterIdx < 0 || ordered[afterIdx + 1]?.id !== before.item.id) {
        throw new EditError(
          'NOT_ADJACENT',
          'clips must be directly adjacent (after immediately precedes before)',
          c.op,
          'beforeItemId'
        );
      }
      const boundary = itemEnd(after.item);
      const from = Math.max(0, boundary - Math.floor(c.durationInFrames / 2));
      const { id, seq } = nextId(edl, 'transition');
      const transition: TransitionItem = {
        id,
        type: 'transition',
        from,
        durationInFrames: c.durationInFrames,
        kind: c.kind,
        afterItemId: c.afterItemId,
        beforeItemId: c.beforeItemId,
      };
      return commit(
        edl,
        replaceItems(edl.tracks, after.track.id, [...after.track.items, transition]),
        seq
      );
    }

    case 'addCaption': {
      const track = requireTrack(edl, c.trackId, c.op);
      if (!TRACK_ITEM_TYPES[track.kind].includes('caption')) {
        throw new EditError(
          'TRACK_KIND_MISMATCH',
          `track "${track.id}" cannot hold captions`,
          c.op,
          'trackId'
        );
      }
      const { id, seq } = nextId(edl, 'caption');
      const caption: CaptionItem = {
        id,
        type: 'caption',
        from: c.from,
        durationInFrames: c.durationInFrames,
        text: c.text,
        style: { ...DEFAULT_CAPTION_STYLE, ...c.style },
      };
      return commit(edl, replaceItems(edl.tracks, track.id, [...track.items, caption]), seq);
    }

    case 'editCaption': {
      const loc = locateItem(edl, c.itemId);
      if (!loc) throw new EditError('ITEM_NOT_FOUND', `no item "${c.itemId}"`, c.op, 'itemId');
      if (loc.item.type !== 'caption') {
        throw new EditError(
          'TRACK_KIND_MISMATCH',
          `cannot editCaption a ${loc.item.type}`,
          c.op,
          'itemId'
        );
      }
      const next: CaptionItem = {
        ...loc.item,
        ...(c.text !== undefined ? { text: c.text } : {}),
        ...(c.durationInFrames !== undefined ? { durationInFrames: c.durationInFrames } : {}),
        style: { ...loc.item.style, ...c.style },
      };
      const items = loc.track.items.map((it) => (it.id === c.itemId ? next : it));
      return commit(edl, replaceItems(edl.tracks, loc.track.id, items), edl.seq);
    }

    case 'setCrop': {
      const loc = locateItem(edl, c.itemId);
      if (!loc) throw new EditError('ITEM_NOT_FOUND', `no item "${c.itemId}"`, c.op, 'itemId');
      if (loc.item.type !== 'clip') {
        throw new EditError(
          'TRACK_KIND_MISMATCH',
          `cannot crop a ${loc.item.type}`,
          c.op,
          'itemId'
        );
      }
      const next: ClipItem = { ...loc.item, crop: { ...c.crop } };
      const items = loc.track.items.map((it) => (it.id === c.itemId ? next : it));
      return commit(edl, replaceItems(edl.tracks, loc.track.id, items), edl.seq);
    }

    case 'setFit': {
      const loc = locateItem(edl, c.itemId);
      if (!loc) throw new EditError('ITEM_NOT_FOUND', `no item "${c.itemId}"`, c.op, 'itemId');
      if (loc.item.type !== 'clip') {
        throw new EditError(
          'TRACK_KIND_MISMATCH',
          `cannot set fit on a ${loc.item.type}`,
          c.op,
          'itemId'
        );
      }
      const next: ClipItem = { ...loc.item, fit: c.fit };
      const items = loc.track.items.map((it) => (it.id === c.itemId ? next : it));
      return commit(edl, replaceItems(edl.tracks, loc.track.id, items), edl.seq);
    }

    case 'addGif': {
      // The reducer is pure: a `query` must already have been resolved to a
      // concrete `src` upstream (see the agent's resolution pass).
      if (!c.src) {
        throw new EditError('INVALID_COMMAND', 'addGif requires a resolved src', c.op, 'src');
      }
      // Back-fill the overlay track for EDLs created before overlays existed,
      // so addGif works on legacy projects regardless of caller.
      let working = edl;
      if (!findTrack(working, c.trackId) && c.trackId === DEFAULT_TRACK_IDS.overlay) {
        working = {
          ...working,
          tracks: [
            ...working.tracks,
            { id: DEFAULT_TRACK_IDS.overlay, kind: 'overlay', items: [] },
          ],
        };
      }
      const track = requireTrack(working, c.trackId, c.op);
      if (!TRACK_ITEM_TYPES[track.kind].includes('gif')) {
        throw new EditError(
          'TRACK_KIND_MISMATCH',
          `track "${track.id}" cannot hold gifs`,
          c.op,
          'trackId'
        );
      }
      const { id, seq } = nextId(working, 'gif');
      const gif: GifItem = {
        id,
        type: 'gif',
        from: c.at,
        durationInFrames: c.durationInFrames,
        src: c.src,
        rect: { ...c.rect },
      };
      return commit(working, replaceItems(working.tracks, track.id, [...track.items, gif]), seq);
    }

    case 'addSfx': {
      const track = requireTrack(edl, c.trackId, c.op);
      if (!TRACK_ITEM_TYPES[track.kind].includes('sfx')) {
        throw new EditError(
          'TRACK_KIND_MISMATCH',
          `track "${track.id}" cannot hold sfx`,
          c.op,
          'trackId'
        );
      }
      const { id, seq } = nextId(edl, 'sfx');
      // An SFX is a one-shot: default its timeline length to the clip's OWN
      // natural duration (from the library) so the block matches what actually
      // plays — not a fixed 1s stub. An explicit durationInFrames still wins
      // (e.g. the user trimmed it); unknown ids fall back to the 1s default.
      const spec = SFX_LIBRARY[c.sfxId];
      const naturalFrames = spec ? Math.max(1, secToFrames(spec.durationSec)) : DEFAULT_SFX_FRAMES;
      const sfx: SfxItem = {
        id,
        type: 'sfx',
        from: c.at,
        durationInFrames: c.durationInFrames ?? naturalFrames,
        sfxId: c.sfxId,
        ...(c.volume !== undefined ? { volume: c.volume } : {}),
      };
      return commit(edl, replaceItems(edl.tracks, track.id, [...track.items, sfx]), seq);
    }

    case 'setLayout': {
      // Placement-only: does not touch tracks/items, so no commit/normalize.
      return { ...edl, layout: c.layout };
    }

    case 'setTitle': {
      const base: TitleOverlay = edl.title ?? { text: '', style: DEFAULT_TITLE_STYLE };
      const text = c.text ?? base.text;
      if (!text) {
        throw new EditError(
          'INVALID_COMMAND',
          'setTitle requires text for a new title',
          c.op,
          'text'
        );
      }
      const title: TitleOverlay = { text, style: { ...base.style, ...c.style } };
      return { ...edl, title };
    }

    default: {
      // Exhaustiveness guard — unreachable when the discriminated union is complete.
      const _never: never = c;
      throw new EditError(
        'INVALID_COMMAND',
        `unhandled command: ${JSON.stringify(_never)}`,
        'unknown'
      );
    }
  }
}

/** Apply a sequence of commands left-to-right. Pure. */
export function applyMany(edl: Edl, cmds: EditCommand[]): Edl {
  return cmds.reduce(apply, edl);
}

/**
 * Best-effort inverse command for undo, computed against the PRE-apply EDL.
 * Returns null when a command is not cleanly invertible as a single command
 * (split, remove, and crop-clears) — for those, use snapshot-based {@link History}.
 *
 * Because ids are deterministic (`${prefix}-${edl.seq}`), the inverse of an
 * `add*` command is a `remove` of the id `apply` is about to mint.
 */
export function invert(edl: Edl, cmd: EditCommand): EditCommand | null {
  switch (cmd.op) {
    case 'addClip':
      return { op: 'remove', itemId: `clip-${edl.seq}` };
    case 'addCaption':
      return { op: 'remove', itemId: `cap-${edl.seq}` };
    case 'addSfx':
      return { op: 'remove', itemId: `sfx-${edl.seq}` };
    case 'addGif':
      return { op: 'remove', itemId: `gif-${edl.seq}` };
    case 'addTransition':
      return { op: 'remove', itemId: `trans-${edl.seq}` };
    case 'move': {
      const loc = locateItem(edl, cmd.itemId);
      if (!loc) return null;
      return { op: 'move', itemId: cmd.itemId, toTrackId: loc.track.id, at: loc.item.from };
    }
    case 'trim': {
      const loc = locateItem(edl, cmd.itemId);
      if (!loc || loc.item.type !== 'clip') {
        if (loc)
          return { op: 'trim', itemId: cmd.itemId, durationInFrames: loc.item.durationInFrames };
        return null;
      }
      return {
        op: 'trim',
        itemId: cmd.itemId,
        sourceInSec: loc.item.source.sourceInSec,
        sourceOutSec: loc.item.source.sourceOutSec,
        durationInFrames: loc.item.durationInFrames,
      };
    }
    case 'setCrop': {
      const loc = locateItem(edl, cmd.itemId);
      if (!loc || loc.item.type !== 'clip' || !loc.item.crop) return null; // cannot express "unset crop"
      return { op: 'setCrop', itemId: cmd.itemId, crop: loc.item.crop };
    }
    case 'setFit': {
      const loc = locateItem(edl, cmd.itemId);
      if (!loc || loc.item.type !== 'clip') return null;
      // Absent fit means the default ('blur'); restore that explicitly.
      return { op: 'setFit', itemId: cmd.itemId, fit: loc.item.fit ?? 'blur' };
    }
    case 'setLayout':
      // Always invertible: restore the layout the EDL had before.
      return { op: 'setLayout', layout: edl.layout };
    case 'setTitle':
      // Restorable only if a title already existed; creating one from nothing
      // cannot be expressed as "clear the title" — fall back to snapshot undo.
      return edl.title ? { op: 'setTitle', text: edl.title.text, style: edl.title.style } : null;
    case 'split':
    case 'remove':
    case 'editCaption':
      return null;
    default: {
      const _never: never = cmd;
      void _never;
      return null;
    }
  }
}
